/**
 * Manages public provider acquisition popups for unauthenticated visitors.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useProviderAcquisitionCampaigns } from '../../hooks/useProviderAcquisitionCampaigns';
import ProviderAcquisitionPopup from './ProviderAcquisitionPopup';
import { useOverlayAdCoordinator } from '../../ads/OverlayAdCoordinator';
import { acquireOverlayWhenAvailable } from '../../ads/acquireOverlayWhenAvailable';
import { isAdRestrictedPath } from '../../../shared/adsense/adSurfacePolicy';

const ProviderAcquisitionPopupManager: React.FC = () => {
  const {
    enabled,
    activeCampaign,
    showPopup,
    dismissPopup,
    handleCtaClick,
    isPopupDue,
  } = useProviderAcquisitionCampaigns();
  const [isShown, setIsShown] = useState(false);
  const triggerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitHandlerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const abortRef = useRef({ aborted: false });
  const showingRef = useRef(false);
  const { release } = useOverlayAdCoordinator();
  const claimId = activeCampaign ? `provider-${activeCampaign.id}` : 'provider-none';

  useEffect(() => {
    setIsShown(false);
    showingRef.current = false;
    abortRef.current.aborted = true;
    abortRef.current = { aborted: false };
    if (triggerTimerRef.current) {
      clearTimeout(triggerTimerRef.current);
      triggerTimerRef.current = null;
    }
    if (exitHandlerRef.current) {
      document.removeEventListener('mouseout', exitHandlerRef.current);
      exitHandlerRef.current = null;
    }
  }, [activeCampaign?.id]);

  useEffect(() => {
    if (!enabled || !activeCampaign || isShown) {
      return;
    }
    if (typeof window !== 'undefined' && isAdRestrictedPath(window.location.pathname)) {
      return;
    }

    const runShow = () => {
      if (typeof window !== 'undefined' && isAdRestrictedPath(window.location.pathname)) {
        return;
      }
      if (showingRef.current || isShown) return;
      showingRef.current = true;
      const signal = { aborted: false };
      abortRef.current = signal;
      void (async () => {
        const ok = await acquireOverlayWhenAvailable('provider_acquisition', claimId, {
          signal,
          timeoutMs: 45_000,
        });
        if (!ok || signal.aborted) {
          showingRef.current = false;
          return;
        }
        setIsShown(true);
        void showPopup(activeCampaign);
      })();
    };

    if (isPopupDue(activeCampaign)) {
      if (activeCampaign.trigger_type === 'time_delay') {
        const delayMs = (activeCampaign.trigger_value ?? 5) * 1000;
        triggerTimerRef.current = setTimeout(runShow, delayMs);
      } else {
        runShow();
      }
      return () => {
        if (triggerTimerRef.current) clearTimeout(triggerTimerRef.current);
      };
    }

    if (activeCampaign.trigger_type === 'time_delay') {
      const delayMs = (activeCampaign.trigger_value ?? 5) * 1000;
      triggerTimerRef.current = setTimeout(runShow, delayMs);
    }

    if (activeCampaign.trigger_type === 'exit_intent') {
      exitHandlerRef.current = (e: MouseEvent) => {
        if (e.clientY <= 0) {
          runShow();
        }
      };
      document.addEventListener('mouseout', exitHandlerRef.current);
    }

    if (activeCampaign.trigger_type === 'scroll_percentage') {
      const onScroll = () => {
        const doc = document.documentElement;
        const height = doc.scrollHeight - doc.clientHeight;
        const pct = height > 0 ? (doc.scrollTop / height) * 100 : 0;
        if (pct >= (activeCampaign.trigger_value ?? 50)) {
          runShow();
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }

    return () => {
      abortRef.current.aborted = true;
      if (triggerTimerRef.current) clearTimeout(triggerTimerRef.current);
      if (exitHandlerRef.current) {
        document.removeEventListener('mouseout', exitHandlerRef.current);
      }
    };
  }, [enabled, activeCampaign, isShown, isPopupDue, showPopup, claimId]);

  useEffect(() => {
    return () => {
      abortRef.current.aborted = true;
      if (activeCampaign) {
        release('provider_acquisition', `provider-${activeCampaign.id}`);
      }
    };
  }, [activeCampaign, release]);

  if (!enabled || !activeCampaign?.content || !isShown) {
    return null;
  }

  return (
    <ProviderAcquisitionPopup
      campaign={activeCampaign}
      content={activeCampaign.content}
      onDismiss={(action) => {
        setIsShown(false);
        showingRef.current = false;
        release('provider_acquisition', claimId);
        void dismissPopup(activeCampaign, action ?? 'dismissed');
      }}
      onCtaClick={(type, url) => {
        release('provider_acquisition', claimId);
        void handleCtaClick(activeCampaign, type, url);
      }}
    />
  );
};

export default ProviderAcquisitionPopupManager;
